import React, { useState, useEffect, use } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MailOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/auth';
import { useTheme } from '../../contexts/ThemeContext';
import { DynamicForm } from '../../components/form/DynamicForm';
import Alert from '../../components/ui/alert';
import Loading from '../../components/Loading';
import LoginSidebar from '../../components/sidebar/LoginSidebar';
import { useMediaQuery } from 'react-responsive';
import { extractTenantFromUrl } from '../../utils/themeUtils';

export const SignIn = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { theme, setTenantThemeData, resetToDefaultTheme, loadTenantTheme, setWhitelabelEnabled } = useTheme();

  const urlTenantIdentifier = extractTenantFromUrl();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", identificador: urlTenantIdentifier || "" });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (urlTenantIdentifier) {
      loadTenantTheme(urlTenantIdentifier, false).then(() => setWhitelabelEnabled(true));
    } else {
      resetToDefaultTheme();
    }
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const submitForm = async (values) => {
    try {
      setLoading(true);
      setError(null);
      const result = await signIn({
        username: values.username,
        password: values.password,
        identificador: urlTenantIdentifier || values.identificador
      });

      if (result && result.data && result.data.response) {
        const authResponse = result.data.response;
        
        navigate("/reports");
        
        // Carregar tema do tenant em paralelo (não bloquear navegação)
        if (authResponse.tenant && authResponse.tenant.id) {
          
          // Executar em paralelo sem await para não bloquear
          Promise.resolve().then(() => {
            try {
              // Carregar tema do tenant diretamente dos dados do login
              // Isso evita uma chamada de API extra e garante que o tema seja aplicado imediatamente
              setTenantThemeData(authResponse.tenant);
              
            } catch (themeError) {
              console.warn('⚠️ Erro ao carregar tema do tenant:', themeError);
            }
            
            console.groupEnd();
          });
        }
      }
    } catch (error) {
      console.error('Erro no login:', error);
      let msg = 'Erro ao fazer login. Verifique suas credenciais.';
      const notifications = error?.response?.data?.notifications;
      if (Array.isArray(notifications) && notifications.length > 0) {
        msg = notifications.map(n => n.message).join(' ');
      } else if (error?.response?.data?.message) {
        msg = error.response.data.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Função para lidar com a tecla Enter no campo identificador
  const handleIdentificadorKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const identificadorOk = urlTenantIdentifier || form.identificador;
      if (form.username && form.password && identificadorOk) {
        submitForm(form);
      }
    }
  };

  const isTabletOrMobile = useMediaQuery({ maxWidth: 1024 });

  const baseQuestions = [
    {
      id: 'username',
      label: 'Login',
      type: 'text',
      placeholder: 'Digite seu login',
      style: { padding: '10px' },
      prefixIcon: <MailOutlined />
    },
    {
      id: 'password',
      label: 'Senha',
      type: 'password',
      placeholder: 'Digite sua senha',
      style: { padding: '10px' },
      prefixIcon: <LockOutlined />,
      showStrengthIndicator: false,
      requireUppercase: false,
      requireLowercase: false,
      requireNumbers: false,
      requireSpecialChars: false,
      minLength: 1,
      customValidationMessage: null
    },
  ];

  if (!urlTenantIdentifier) {
    baseQuestions.push({
      id: 'identificador',
      label: 'Empresa',
      type: 'text',
      placeholder: 'Digite o nome da empresa',
      style: { padding: '10px' },
      prefixIcon: <UserOutlined />,
      onKeyPress: handleIdentificadorKeyPress
    });
  }

  const formConfig = [{ columns: 1, questions: baseQuestions }];

  return (
    <div style={{ 
      height: '100vh', 
      width: '100vw', 
      display: 'flex',
      overflow: 'hidden'
    }}>
      {/* Sidebar Esquerda - 50% */}
      {!isTabletOrMobile && <LoginSidebar />}

      {/* Área do Formulário - 50% */}
      <div style={{
        width: isTabletOrMobile ? '100%' : '50%',
        background: theme.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
      }}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: theme.primary,
              marginBottom: '8px'
            }}>
              Bem-vindo de volta
            </h2>
            <p style={{
              fontSize: '16px',
              color: theme.secondary
            }}>
              Acesse sua conta para continuar
            </p>
          </div>

          {/* Alert de erro */}
          {error && (
            <Alert
              message="Erro ao fazer login"
              description={error}
              type="error"
              showIcon
              closable
              style={{ marginBottom: '16px' }}
              onClose={() => setError(null)}
            />
          )}

          {/* Formulário */}
          {loading ? (
              <Loading />
            ) : (
          <DynamicForm
            formConfig={formConfig}
            values={form}
            style={{
              backgroundColor: theme.background,
            }}
            setValues={setForm}
            submitText="Entrar"
            onSubmit={submitForm}
            inputPadding="12px 16px"
            submitButtonStyle={{
              height: '40px',
              fontSize: '16px',
              fontWeight: '600',
              backgroundColor: theme.primary,
              border: 'none',
              borderRadius: '8px',
            }}
            extraFooterContent={
              <Link
                to="/forgot-password"
                style={{
                  color: theme.primary,
                  textDecoration: 'none',
                  fontSize: '14px',
                  display: 'block',
                  textAlign: 'center',
                  marginTop: '10px'
                }}
              >
                Esqueceu a senha?
              </Link>
            }
          />
          )}
        </div>
      </div>
    </div>
  );
};